import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    SubscribeGoogleMeetWebhooks as GoogleMeetWebhooksSubscribeMutation,
    SubscribeGoogleMeetWebhooksVariables as GoogleMeetWebhooksSubscribeMutationVariables,
    GetMeetIntegration as GetMeetIntegrationQuery,
    GoogleMeetWebhooksSubscribeInput,
} from 'nutshell-graphql-types';

import GET_MEET_INTEGRATION from '../../../../scheduler/location/conferencing-modals/graphql/queries/get-meet-integration.graphql';

import SUBSCRIBE_GOOGLE_MEET_WEBHOOKS from '../mutations/subscribe-google-meet-webhooks.graphql';

export function useSubscribeGoogleMeetWebhooks(): {
    subscribeGoogleMeetWebhooks: (input: GoogleMeetWebhooksSubscribeInput) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} {
    const [subscribeGoogleMeetWebhooks, {loading, error}] = useMutation<
        GoogleMeetWebhooksSubscribeMutation,
        GoogleMeetWebhooksSubscribeMutationVariables
    >(SUBSCRIBE_GOOGLE_MEET_WEBHOOKS);

    return {
        subscribeGoogleMeetWebhooks: (input: GoogleMeetWebhooksSubscribeInput) => {
            return subscribeGoogleMeetWebhooks({
                variables: {input},
                optimisticResponse: {
                    googleMeetWebhooksSubscribe: {
                        __typename: 'GoogleMeetWebhooksSubscribePayload',
                        googleMeetIntegration: {
                            __typename: 'GoogleMeetIntegration',
                            isEnabledMeetSync: true,
                            isEnabledCalendarSync: false,
                            email: '',
                        },
                    },
                },
                update: (proxy, mutationResults) => {
                    const cacheResult: GetMeetIntegrationQuery | null | undefined = proxy.readQuery(
                        {
                            query: GET_MEET_INTEGRATION,
                        }
                    );

                    if (!cacheResult || !cacheResult.loggedInUser) {
                        return;
                    }

                    const updatedGoogleMeetIntegration =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.googleMeetWebhooksSubscribe &&
                        mutationResults.data.googleMeetWebhooksSubscribe.googleMeetIntegration;

                    if (!updatedGoogleMeetIntegration) return;

                    proxy.writeQuery({
                        query: GET_MEET_INTEGRATION,
                        data: {
                            loggedInUser: {
                                ...cacheResult.loggedInUser,
                                googleMeetIntegration: updatedGoogleMeetIntegration,
                            },
                        },
                    });
                },
            });
        },
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
}
